<template>
  <div>
    <label class="form-label" for="policyGroup">Policy Group</label>
    <multiselect
      id="policyGroup"
      v-model="selectedGroup"
      :options="policyGroupsWithOptions"
      placeholder="Search or select a group"
      label="name"
      track-by="ID"
      @select="handleSelect"
      @search-change="searchQuery = $event"
      :allow-empty="true"
    >
      <template #noResult><span>No groups found. Select "Create a new group..." to add one.</span></template>
    </multiselect>

    <vue-final-modal v-model="showCreateModal" classes="modal-container" content-class="modal-content">
      <div class="modal-header">
        <h5 class="modal__title text-white">Create New Policy Group</h5>
        <button type="button" class="btn-close" @click="showCreateModal = false"></button>
      </div>
      <div class="modal__body">
        <form @submit.prevent="requestCreateNewGroup">
          <div class="mb-3">
            <label for="newGroupName" class="form-label">Group Name</label>
            <input
              type="text"
              id="newGroupName"
              v-model="newGroup.name"
              class="form-control"
              placeholder="Enter new group name"
              required
            />
          </div>
          <div class="mb-3">
            <label for="newGroupDesc" class="form-label">Description</label>
            <textarea
              id="newGroupDesc"
              v-model="newGroup.description"
              class="form-control"
              placeholder="Enter group description..."
              rows="4"
              required
            ></textarea>
          </div>
          <div class="modal__action border-0 p-0">
            <button type="button" class="btn btn-secondary" @click="showCreateModal = false">Cancel</button>
            <button type="submit" class="btn btn-primary">Create Group</button>
          </div>
        </form>
      </div>
    </vue-final-modal>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect';

export default {
  name: 'PolicyGroupSelector',
  components: {
    Multiselect,
  },
  props: {
    groups: { type: Array, required: true },
    initialGroupId: { type: Number, default: null },
    canCreateGroup: { type: Boolean, default: false },
  },
  emits: ['group-selected', 'group-created', 'create-group-request'],
  data() {
    return {
      selectedGroup: null,
      showCreateModal: false,
      newGroup: {
        name: '',
        description: '',
      },
      searchQuery: '',
      ungroupOption: { ID: 0, name: 'Remove from Group' },
    };
  },
  watch: {
    // Watch for any changes to the groups list (e.g., after a create/edit)
    groups: {
      handler() {
        this.syncSelectionWithProps();
      },
      deep: true
    },
    // Watch for changes to the initial ID (e.g., when the parent form loads its data)
    initialGroupId() {
      this.syncSelectionWithProps();
    }
  },
  computed: {
    policyGroupsWithOptions() {
      const options = [];

      // Only show "Remove from Group" if the policy is already in a group.
      if (this.initialGroupId && this.initialGroupId !== 0) {
        options.push(this.ungroupOption);
      }

      options.push(...this.groups);
      if (this.canCreateGroup) {
        options.push({ ID: '---new---', name: 'Create a new group...' });
      }
      return options;
    },
  },
  mounted() {
    // Set the initial value when the component is first mounted
    this.syncSelectionWithProps();
  },
  methods: {
    // ADDED: A central method to sync the component's state with its props
    syncSelectionWithProps() {
      if (this.initialGroupId === null || this.initialGroupId === 0) {
        this.selectedGroup = null; // Show placeholder for ungrouped policies
      } else if (this.initialGroupId) {
        this.selectedGroup = this.groups.find(g => g.ID === this.initialGroupId) || null;
      } else {
        this.selectedGroup = null;
      }
    },
    handleSelect(selectedOption) {
      if (selectedOption && selectedOption.ID === '---new---') {
        this.newGroup.name = this.searchQuery;
        this.showCreateModal = true;
        this.selectedGroup = null;
      } else {
        this.$emit('group-selected', selectedOption);
      }
    },
    requestCreateNewGroup() {
      const name = this.newGroup.name.trim();
      const description = this.newGroup.description.trim();

      if (!name || !description) {
        this.$toast.error("Group Name and Description are required.");
        return;
      }

      const nameExists = this.groups.some(group => group.name.toLowerCase() === name.toLowerCase());
      if (nameExists) {
        this.$toast.error("A policy group with this name already exists.");
        return;
      }

      this.$emit('create-group-request', { name, description });
      this.showCreateModal = false;
      this.newGroup = { name: '', description: '' };
    },
  },
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.css"></style>

<style scoped>
.form-label {
  color: #00A0C1;
  margin-bottom: 0.5rem;
}

:deep(.modal-content) {
  width: 500px;
}

.modal__action {
  display: flex;
  justify-content: center; /* Center the buttons */
  gap: 1rem; /* Add space between the buttons */
  flex-shrink: 0;
  padding: 1rem 0 0;
}
</style>